import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import type { IRedeemableAttachedConfiguration } from "./Configurations";
import type {
  IListRedeemables,
  IListRedeemablesParams,
  IMenuItemRestriction,
} from "./Redeemables";
import { RedeemablesTypes } from "@/components/screens/Redeemables/Shared/Types/ListRedeemablesTypes";

export enum DiscountApplicableTo {
  OnPremise = 0,
  Collection = 1,
  All = 2,
  Delivery = 3,
}

export enum DiscountType {
  Percentage = 0,
  Fixed = 1,
  Custom = 2,
}

export interface IListDiscount {
  active: boolean;
  cap_per_head: number;
  code: string;
  id?: ID;
  message: string;
  percentage: number;
  balance?: number;
  created_at: string;
  updated_at: string;
}

export interface IDiscount extends IListDiscount {
  nano_id?: string;
  identifier?: string;
  fixed_amount: number;
  applicable_to: number;
  applicable_weekdays: number[];
  cap: null | number;
  menu_item_restrictions: Array<IMenuItemRestriction>;
  discounted_menu_only: boolean;
  end_date: string;
  end_time: string;
  service_charge_on_discount: boolean;
  configuration: IRedeemableAttachedConfiguration;
  start_date: string;
  start_time: string;
  timezone: string;
  discount_type: DiscountType;
  metadata: {
    menu_items: Array<{
      id: ID;
      name: string;
    }>;
  };
}

export interface IListDiscountParams {
  search?: string;
  isVenueSpecific?: boolean;
  page?: number;
  page_size?: number;
}

export interface IListDiscountResponse
  extends APIPaginatedResponse<IListDiscount> {}

export interface IListDiscountResponseAll {
  pages: Array<IListDiscountResponse>;
  pageParams: number[];
}

export interface IRetrieveDiscountParams {
  id: ID;
}

export interface IDeleteDiscountParams extends IRetrieveDiscountParams {}

export interface ICreateDiscountParams {
  code: string;
  message: string;
  start_date: string;
  end_date: string;
  applicable_to: DiscountApplicableTo;
  discount_type: DiscountType;
  fixed_amount?: number;
  percentage?: number;
  active: boolean;
  applicable_weekdays: Array<number>;
}

export interface IUpdateDiscountParams extends ICreateDiscountParams {
  id: ID;
  start_time: string;
  end_time: string;
}

export interface IDiscountService {
  list: (
    params?: IListDiscountParams,
  ) => UseQueryResult<IListDiscountResponse, Error>;
  listAll: (
    params?: IListDiscountParams,
  ) => UseInfiniteQueryResult<IListDiscountResponseAll, Error>;
  retrieve: (
    params: IRetrieveDiscountParams,
  ) => UseQueryResult<IDiscount, Error>;
  create: (
    listParams?: IListRedeemablesParams,
  ) => UseMutationResult<IDiscount, Error, ICreateDiscountParams>;
  update: () => UseMutationResult<IDiscount, Error, IUpdateDiscountParams>;
  partialUpdate: () => UseMutationResult<
    IDiscount,
    Error,
    Partial<IUpdateDiscountParams>
  >;
  delete: () => UseMutationResult<boolean, Error, IDeleteDiscountParams>;
}

export class DiscountService implements IDiscountService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(
    params?: IListDiscountParams,
  ): UseQueryResult<IListDiscountResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );

    const { isVenueSpecific = false, ...restParams } = updatedParams;
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "discounts", "list"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async () => {
        const {
          data: { results, next, count, previous },
        } = await this.api.get<AxiosResponse<IListDiscountResponse>>(
          "/promotions/discount_codes/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
            params: restParams,
          },
        );

        return {
          results,
          next,
          count,
          previous,
        };
      },
    });
  }

  listAll(
    params?: IListDiscountParams,
  ): UseInfiniteQueryResult<IListDiscountResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );

    const { isVenueSpecific = false, ...restParams } = updatedParams;
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "discounts", "list-all"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<IListDiscountResponse>
        >("/promotions/discount_codes/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: isVenueSpecific,
          params: { ...restParams, page: pageParam, page_size: 50 },
        });

        return data;
      },
      getNextPageParam: (lastPage: IListDiscountResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  retrieve(params: IRetrieveDiscountParams): UseQueryResult<IDiscount, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "discounts", "retrieve", params.id],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IDiscount>>(
          `/promotions/discount_codes/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      enabled: !!params.id,
    });
  }

  create(
    listParams?: IListRedeemablesParams,
  ): UseMutationResult<IDiscount, Error, ICreateDiscountParams> {
    const queryClient = useQueryClient();
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams || {}).filter(
        ([, value]) => value !== undefined || value !== null,
      ),
    );
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "discounts", "create"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<IDiscount>>(
          "/promotions/discount_codes/",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onMutate: async (newDiscount) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
        const previousDiscounts = queryClient.getQueryData<IListRedeemables>(
          buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        );

        if (previousDiscounts) {
          queryClient.setQueryData<IListRedeemables>(
            buildQueryKey({
              queryKey: ["redeemables", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            (old) => {
              return {
                ...old,
                results: [
                  ...old.results,
                  {
                    id: -1,
                    active: true,
                    redeemable_type: RedeemablesTypes.Discount,
                    start_date: newDiscount.start_date,
                    configuration_id: null,
                    end_date: newDiscount.end_date,
                    code: newDiscount.code,
                    message: newDiscount?.message || "",
                  },
                ],
                count: old.count + 1,
                next: old.next,
                previous: old.previous,
              };
            },
          );
        }

        return { previousDiscounts };
      },
      onError: (err, newDiscount, context) => {
        if (context?.previousDiscounts) {
          queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            context.previousDiscounts,
          );
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }

  update(): UseMutationResult<IDiscount, Error, IUpdateDiscountParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "discounts", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<IDiscount>>(
          `/promotions/discount_codes/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: restParams,
          },
        );
        return data;
      },
      onMutate: async (newDiscount) => {
        const { id, ...restParams } = newDiscount;

        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "discounts", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        });
        const previousDiscount = await queryClient.getQueryData<IDiscount>(
          buildQueryKey({
            queryKey: ["redeemables", "discounts", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        );
        if (previousDiscount) {
          await queryClient.setQueryData<IDiscount>(
            buildQueryKey({
              queryKey: ["redeemables", "discounts", "retrieve", id],
              venueID: this.api.selectedVenue,
              params: { id },
            }),
            (old) => {
              return {
                ...old,
                ...restParams,
              };
            },
          );
        }
        return { previousDiscount };
      },
      onError: async (err, newDiscount, context) => {
        if (context?.previousDiscount) {
          await queryClient.setQueryData(
            buildQueryKey({
              queryKey: [
                "redeemables",
                "discounts",
                "retrieve",
                newDiscount.id,
              ],
              venueID: this.api.selectedVenue,
              params: { id: newDiscount.id },
            }),
            context.previousDiscount,
          );
        }
      },
      onSettled: async (data, error, variables) => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "discounts", "retrieve", variables.id],
            venueID: this.api.selectedVenue,
            params: { id: variables.id },
          }),
        });
      },
    });
  }

  partialUpdate(): UseMutationResult<
    IDiscount,
    Error,
    Partial<IUpdateDiscountParams>
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "discounts", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<IDiscount>>(
          `/promotions/discount_codes/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: restParams,
          },
        );
        return data;
      },
      onMutate: async (newDiscount) => {
        const { id, ...restParams } = newDiscount;

        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "discounts", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        });
        const previousDiscount = await queryClient.getQueryData<IDiscount>(
          buildQueryKey({
            queryKey: ["redeemables", "discounts", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        );
        if (previousDiscount) {
          await queryClient.setQueryData<IDiscount>(
            buildQueryKey({
              queryKey: ["redeemables", "discounts", "retrieve", id],
              venueID: this.api.selectedVenue,
              params: { id },
            }),
            (old) => {
              return {
                ...old,
                ...restParams,
              };
            },
          );
        }
        return { previousDiscount };
      },
      onError: async (err, newDiscount, context) => {
        if (context?.previousDiscount) {
          await queryClient.setQueryData(
            buildQueryKey({
              queryKey: [
                "redeemables",
                "discounts",
                "retrieve",
                newDiscount.id,
              ],
              venueID: this.api.selectedVenue,
              params: { id: newDiscount.id },
            }),
            context.previousDiscount,
          );
        }
      },
      onSettled: async (data, error, variables) => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "discounts", "retrieve", variables.id],
            venueID: this.api.selectedVenue,
            params: { id: variables.id },
          }),
        });
      },
    });
  }

  delete(): UseMutationResult<boolean, Error, IDeleteDiscountParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "discounts", "delete"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.delete<AxiosResponse<string>>(
          `/promotions/discount_codes/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data === "";
      },
    });
  }
}
